/**
 * ApiResponseValidator Service
 * Validates API responses before processing to catch structure issues early
 */

class ApiResponseValidator {
  /**
   * Validate payment/payout response structure
   * @param {Object} response - API response
   * @throws {Error} If response structure is invalid
   * @returns {Object} Validated response
   */
  static validatePayoutResponse(response) {
    if (!response) {
      throw new Error('Empty response from payout API');
    }

    // Check for error in response
    if (response.error === true) {
      const error = new Error(response.message || 'Payout operation failed');
      error.apiResponse = response;
      throw error;
    }

    // Validate transactionId or similar identifier
    if (response.transactionId === undefined && response.transaction_id === undefined) {
      console.warn('Warning: Payout response missing transaction identifier', response);
    }

    return response;
  }

  /**
   * Validate registry upload response
   * @param {Object} response - API response
   * @throws {Error} If response structure is invalid
   * @returns {Object} Validated response
   */
  static validateRegistryUpload(response) {
    if (!response) {
      throw new Error('Empty response from registry upload');
    }

    if (response.error === true) {
      const error = new Error(response.message || 'Registry upload failed');
      error.apiResponse = response;
      throw error;
    }

    if (!response.id && !response.uuid && !response.registryId) {
      throw new Error('Invalid registry response: missing ID');
    }

    return response;
  }

  /**
   * Validate registry validation response (after upload, before processing)
   * @param {Object} response - API response
   * @throws {Error} If response structure is invalid or has validation errors
   * @returns {Object} Validated response
   */
  static validateRegistryValidation(response) {
    if (!response) {
      throw new Error('Empty response from registry validation');
    }

    if (response.error === true) {
      const error = new Error(response.message || 'Registry validation failed');
      error.apiResponse = response;
      error.validationErrors = response.errors || [];
      throw error;
    }

    // If there are validation errors in the response, throw them
    if (Array.isArray(response.errors) && response.errors.length > 0) {
      const error = new Error('Registry has validation errors');
      error.validationErrors = response.errors;
      error.apiResponse = response;
      throw error;
    }

    return response;
  }

  /**
   * Validate registry process/reprocess response
   * @param {Object} response - API response
   * @throws {Error} If response structure is invalid
   * @returns {Object} Validated response
   */
  static validateRegistryProcess(response) {
    if (!response) {
      throw new Error('Empty response from registry process');
    }

    if (response.error === true) {
      const error = new Error(response.message || 'Registry processing failed');
      error.apiResponse = response;
      throw error;
    }

    // Processing doesn't necessarily return data, but check for error indicators
    return response;
  }

  /**
   * Validate list/array response
   * @param {Object} response - API response (typically { data: [...], count: N })
   * @throws {Error} If response structure is invalid
   * @returns {Object} Validated response
   */
  static validateListResponse(response) {
    if (!response) {
      throw new Error('Empty response from list API');
    }

    if (response.error === true) {
      const error = new Error(response.message || 'Failed to fetch list');
      error.apiResponse = response;
      throw error;
    }

    // Validate structure
    if (!Array.isArray(response.data) && !Array.isArray(response)) {
      console.warn('Warning: List response missing data array', response);
    }

    return response;
  }

  /**
   * Validate pagination response
   * @param {Object} response - API response with pagination
   * @throws {Error} If response structure is invalid
   * @returns {Object} Validated response
   */
  static validatePaginationResponse(response) {
    if (!response) {
      throw new Error('Empty response from pagination API');
    }

    if (response.error === true) {
      const error = new Error(response.message || 'Failed to fetch paginated data');
      error.apiResponse = response;
      throw error;
    }

    // Check for required pagination fields
    if (!Array.isArray(response.data)) {
      console.warn('Warning: Pagination response missing data array', response);
    }

    if (response.count === undefined) {
      console.warn('Warning: Pagination response missing count field', response);
    }

    return response;
  }

  /**
   * Validate transaction response
   * @param {Object} response - API response
   * @throws {Error} If response structure is invalid
   * @returns {Object} Validated response
   */
  static validateTransactionResponse(response) {
    if (!response) {
      throw new Error('Empty response from transaction API');
    }

    if (response.error === true) {
      const error = new Error(response.message || 'Transaction operation failed');
      error.apiResponse = response;
      throw error;
    }

    if (!response.id && !response.transactionId && !response.transaction_id) {
      console.warn('Warning: Transaction response missing ID', response);
    }

    return response;
  }

  /**
   * Validate generic response for common errors
   * @param {Object} response - API response
   * @throws {Error} If response contains error
   * @returns {Object} Validated response
   */
  static validateResponse(response, options = {}) {
    if (!response) {
      throw new Error(options.emptyMessage || 'Empty response from server');
    }

    // Check for error flag
    if (response.error === true) {
      const error = new Error(response.message || options.errorMessage || 'Operation failed');
      error.apiResponse = response;
      throw error;
    }

    // Check for error code
    if (response.code && response.code !== 0 && response.code !== '0') {
      const error = new Error(response.message || options.errorMessage || 'Operation failed');
      error.code = response.code;
      error.apiResponse = response;
      throw error;
    }

    // Check for success flag explicitly if provided
    if (options.requireSuccess === true && response.success !== true) {
      throw new Error(options.errorMessage || 'Operation was not successful');
    }

    return response;
  }

  /**
   * Validate response and extract data field
   * @param {Object} response - API response
   * @param {string} dataPath - Path to data (e.g., 'data', 'payload.data')
   * @returns {*} Extracted data
   * @throws {Error} If data not found
   */
  static validateAndExtractData(response, dataPath = 'data') {
    this.validateResponse(response);

    const data = this.getNestedValue(response, dataPath);
    if (data === undefined) {
      throw new Error(`Expected data at path '${dataPath}' not found in response`);
    }

    return data;
  }

  /**
   * Get nested value from object
   * @param {Object} obj - Object to query
   * @param {string} path - Path to value (e.g., 'user.profile.name')
   * @returns {*} Value at path or undefined
   */
  static getNestedValue(obj, path) {
    return path.split('.').reduce((current, prop) => (current && current[prop]), obj);
  }

  /**
   * Check if response indicates successful operation
   * @param {Object} response - API response
   * @returns {boolean}
   */
  static isSuccessResponse(response) {
    if (!response) return false;
    return response.error !== true && (!response.code || response.code === 0 || response.code === '0');
  }

  /**
   * Check if response indicates an error
   * @param {Object} response - API response
   * @returns {boolean}
   */
  static isErrorResponse(response) {
    if (!response) return true;
    return response.error === true || (response.code && response.code !== 0 && response.code !== '0');
  }
}

export default ApiResponseValidator;



// WEBPACK FOOTER //
// ./src/Services/ApiResponseValidator.js